import { c as _c } from "react/compiler-runtime";
import { buttonColorStyles, buttonVariantStyles, createColorCompoundVariants } from "@/components/ui/ColorVariants";
import { getOpticalAlignClasses } from "@/components/ui/optical-alignment";
import type { OpticalAlignment } from "@/components/ui/optical-alignment";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";
const buttonVariants = cva("m-0 inline-flex max-w-full shrink-0 appearance-none select-none items-center justify-center whitespace-nowrap border-[length:var(--control-button-border-width)] border-solid border-transparent bg-transparent p-0 [font-weight:var(--font-weight-medium)] text-current [font-size:var(--button-font-size)] [line-height:var(--button-line-height)] [letter-spacing:var(--button-letter-spacing)] [gap:var(--button-gap)] rounded-[var(--button-corner-radius)] pl-[var(--button-padding-left)] pr-[var(--button-padding-right)] h-[var(--button-height)] transition-[border-color,background-color,color,box-shadow,opacity,filter,transform] [transition-duration:160ms] [transition-timing-function:var(--ease-out-quad)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [--button-height:40px] [--button-padding-left:16px] [--button-padding-right:16px] [--button-gap:6px] [--button-font-size:15px] [--button-line-height:15px] [--button-letter-spacing:normal] [--button-icon-size:18px] [--button-corner-radius:var(--control-button-radius)] [--visual-trim-start:var(--button-padding-left)] [--visual-trim-end:var(--button-padding-right)] [&_svg]:pointer-events-none [&_svg]:block [&_svg]:shrink-0 [&_svg]:self-center [&_svg]:h-[var(--button-icon-size)] [&_svg]:w-[var(--button-icon-size)]", {
  variants: {
    variant: {
      ...buttonVariantStyles
    },
    color: {
      primary: "",
      secondary: "",
      danger: "",
      success: "",
      warning: "",
      info: "",
      neutral: ""
    },
    size: {
      xs: "[--button-height:24px] [--button-padding-left:8px] [--button-padding-right:8px] [--button-gap:4px] [--button-font-size:12px] [--button-line-height:12px] [--button-letter-spacing:normal] [--button-icon-size:12px]",
      sm: "[--button-height:var(--control-height-sm)] [--button-padding-left:var(--control-padding-inline-sm)] [--button-padding-right:var(--control-padding-inline-sm)] [--button-gap:4px] [--button-font-size:var(--control-font-size-sm)] [--button-line-height:normal] [--button-letter-spacing:normal] [--button-icon-size:14px]",
      md: "[--button-height:var(--control-height-md)] [--button-padding-left:var(--control-padding-inline-md)] [--button-padding-right:var(--control-padding-inline-md)] [--button-gap:5px] [--button-font-size:var(--control-font-size-md)] [--button-line-height:normal] [--button-letter-spacing:normal] [--button-icon-size:14px]",
      lg: "[--button-height:var(--control-height-lg)] [--button-padding-left:var(--control-padding-inline-lg)] [--button-padding-right:var(--control-padding-inline-lg)] [--button-gap:6px] [--button-font-size:var(--control-font-size-lg)] [--button-line-height:normal] [--button-letter-spacing:normal] [--button-icon-size:16px]",
      xl: "[--button-height:40px] [--button-padding-left:16px] [--button-padding-right:16px] [--button-gap:6px] [--button-font-size:15px] [--button-line-height:15px] [--button-letter-spacing:normal] [--button-icon-size:18px]"
    },
    rounded: {
      true: "[--button-corner-radius:var(--control-button-radius)]",
      false: null
    },
    uniform: {
      true: "w-[var(--button-height)] [--button-padding-left:0px] [--button-padding-right:0px] [--button-gap:0px]",
      false: null
    }
  },
  defaultVariants: {
    variant: "solid",
    color: "secondary",
    size: "md",
    rounded: false,
    uniform: false
  },
  compoundVariants: [{
    variant: "link",
    uniform: false,
    className: "h-auto w-auto px-0 [height:auto] [padding-left:0] [padding-right:0]"
  }, ...createColorCompoundVariants(buttonColorStyles)]
});
export type ButtonProps = {
  asChild?: boolean;
  isLoading?: boolean;
  square?: boolean;
  tooltip?: string;
  color?: "primary" | "secondary" | "danger" | "success" | "warning" | "info" | "neutral";
  opticallyAlign?: OpticalAlignment | OpticalAlignment[];
  ref?: React.Ref<HTMLButtonElement>;
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;
const Button = t0 => {
  const $ = _c(40);
  let children;
  let className;
  let color;
  let disabled;
  let opticallyAlign;
  let props;
  let ref;
  let square;
  let t1;
  let t2;
  let t3;
  let t4;
  let tooltip;
  let uniform;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      color,
      size: t1,
      rounded: t2,
      uniform,
      square,
      asChild: t3,
      isLoading: t4,
      opticallyAlign,
      children,
      tooltip,
      disabled,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = color;
    $[4] = disabled;
    $[5] = opticallyAlign;
    $[6] = props;
    $[7] = ref;
    $[8] = square;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = tooltip;
    $[14] = uniform;
    $[15] = variant;
  } else {
    children = $[1];
    className = $[2];
    color = $[3];
    disabled = $[4];
    opticallyAlign = $[5];
    props = $[6];
    ref = $[7];
    square = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    tooltip = $[13];
    uniform = $[14];
    variant = $[15];
  }
  const size = t1 === undefined ? "md" : t1;
  const rounded = t2 === undefined ? false : t2;
  const asChild = t3 === undefined ? false : t3;
  const isLoading = t4 === undefined ? false : t4;
  const Comp = asChild ? Slot : "button";
  const resolvedVariant = variant ?? "solid";
  const resolvedColor = color ?? (resolvedVariant === "solid" || resolvedVariant === "link" ? "primary" : "secondary");
  const resolvedUniform = uniform ?? square ?? false;
  const isDisabled = disabled || isLoading;
  let t5;
  if ($[16] !== className || $[17] !== isLoading || $[18] !== opticallyAlign || $[19] !== resolvedColor || $[20] !== resolvedUniform || $[21] !== resolvedVariant || $[22] !== rounded || $[23] !== size) {
    t5 = cn(buttonVariants({
      variant: resolvedVariant,
      color: resolvedColor,
      size,
      rounded,
      uniform: resolvedUniform,
      className
    }), getOpticalAlignClasses(opticallyAlign), {
      "pointer-events-none opacity-50 shadow-none": isLoading
    });
    $[16] = className;
    $[17] = isLoading;
    $[18] = opticallyAlign;
    $[19] = resolvedColor;
    $[20] = resolvedUniform;
    $[21] = resolvedVariant;
    $[22] = rounded;
    $[23] = size;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== children || $[26] !== isLoading) {
    t6 = isLoading ? <Spinner size={16} /> : children;
    $[25] = children;
    $[26] = isLoading;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== Comp || $[29] !== isDisabled || $[30] !== props || $[31] !== ref || $[32] !== t5 || $[33] !== t6) {
    t7 = <TooltipTrigger asChild={true}><Comp className={t5} disabled={isDisabled} ref={ref} {...props}>{t6}</Comp></TooltipTrigger>;
    $[28] = Comp;
    $[29] = isDisabled;
    $[30] = props;
    $[31] = ref;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== tooltip) {
    t8 = tooltip && <TooltipContent>{tooltip}</TooltipContent>;
    $[35] = tooltip;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t7 || $[38] !== t8) {
    t9 = <Tooltip>{t7}{t8}</Tooltip>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};
Button.displayName = "Button";
export { Button, buttonVariants };